import TakisEjecucion from '../../components/TakisEjecucion';
import { faltaTakis } from '../../domain/takisCierre';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatInTimeZone } from 'date-fns-tz';
import { getMyShifts, getMyShiftsRange } from '../../api/assignments';
import { DIAS_PARA_CERRAR, fechasParaElegir, nombreDeFecha, restarDias } from '../../domain/fechasDeCierre';
import { useAuth } from '../../components/AuthProvider';
import type { MyShiftRow } from '../../api/assignments';
import {
  listCampaniasDeImpulso, listMisCierres, listSkusDeImpulso, registrarCierreDeTurno,
  yaRegistroCierre,
} from '../../api/impulso';
import type { CampaniaDeImpulso, MiCierre, SkuDeImpulso } from '../../api/impulso';
import {
  ESPACIO, EXHIBICION, INCIDENCIA, MERCADERISTA, OBJECION_NINGUNA,
  PREMIOS_DEL_MERCH, QUIEBRE, cuestionarioDe, entregadosEnTotal, loQueFaltaEnCierre, quedanEnTotal,
  totalUnidades,
} from '../../domain/impulso';
import { compressImage } from '../../lib/compressImage';
import PreciosEnGondola from '../../components/PreciosEnGondola';
import { pideBarrido, preciosParaEnviar } from '../../domain/barridoPrecios';
import type { PrecioGondola } from '../../domain/barridoPrecios';
import type {
  CierreDeTurno, ObjecionElegida, Opcion, OpcionDeObjecion, VentaSku,
} from '../../domain/impulso';

const TZ = 'America/Lima';
const hoyLima = (): string => formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');

/**
 * Cierre de turno — la dinámica de IMPULSO.
 *
 * TERCER paso del circuito, después de marcar ingreso y marcar salida. El orden no es estético: la
 * prioridad es la asistencia, y este formulario viene después. Por eso vive en su propia pantalla y
 * no dentro de la tarjeta del turno — igual que canjes, y por la misma razón que dejó escrita ese
 * cambio: en las tiendas no dejan usar el celular, así que se llena al terminar, de noche, y a
 * veces pasada la medianoche, cuando la tarjeta del turno de hoy ya no se pinta.
 *
 * **La fecha se elige.** Arranca en hoy porque es el caso normal, pero quien no alcanzó a llenarlo
 * puede declarar el día del turno. El servidor marca `registro_tardio` igual: elegir otra fecha no
 * borra que se llenó después.
 */
function Grupo({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="mb-4 rounded-xl border border-gray-200 bg-white p-4">
      <h2 className="mb-3 text-base font-bold text-gray-900">{titulo}</h2>
      {children}
    </section>
  );
}

function Elegir({
  id, label, valor, opciones, onChange,
}: {
  id: string; label: string; valor: string; opciones: readonly Opcion[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="mb-3">
      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={id}>{label}</label>
      <select
        id={id}
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-[44px] w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm"
      >
        {opciones.map((o) => (
          <option key={o.valor} value={o.valor}>{o.etiqueta}</option>
        ))}
      </select>
    </div>
  );
}

function Texto({
  id, label, valor, onChange, placeholder, ayuda,
}: {
  id: string; label: string; valor: string; onChange: (v: string) => void;
  placeholder?: string; ayuda?: string;
}) {
  return (
    <div className="mb-3">
      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={id}>{label}</label>
      {ayuda && <p className="mb-1 text-xs text-gray-500">{ayuda}</p>}
      <textarea
        id={id}
        rows={3}
        value={valor}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
      />
    </div>
  );
}

/**
 * Las objeciones de los que NO compraron: varias casillas, y cada una marcada abre su pregunta.
 *
 * Son VARIAS porque en un turno se escucha más de una cosa, y con opción única las otras
 * desaparecían: el turno terminaba leyéndose más simple de lo que fue. Y cada una abre su propio
 * campo porque el valor está en el detalle — «el precio» a secas no dice el precio de qué, ni
 * contra qué promoción de la otra marca lo compararon, que es justo el dato que se perdía.
 *
 * Desmarcar borra el texto a propósito: la API manda el comentario colgado de su objeción, así que
 * un texto sobreviviente a la casilla apagada viajaría huérfano o, peor, pegado a otra respuesta.
 */
function Objeciones({
  elegidas, onChange, opciones,
}: {
  elegidas: ObjecionElegida[];
  onChange: (v: ObjecionElegida[]) => void;
  /** Las de ESTA campaña. No hay una lista única: cada cuestionario trae la suya. */
  opciones: readonly OpcionDeObjecion[];
}) {
  function alternar(valor: string, ahoraMarcada: boolean) {
    if (!ahoraMarcada) { onChange(elegidas.filter((o) => o.valor !== valor)); return; }
    // «Ninguna en particular» es excluyente en los dos sentidos: no se puede declarar que no hubo
    // objeciones y al mismo tiempo cuál fue.
    if (valor === OBJECION_NINGUNA) { onChange([{ valor, comentario: '' }]); return; }
    onChange([...elegidas.filter((o) => o.valor !== OBJECION_NINGUNA), { valor, comentario: '' }]);
  }

  function comentar(valor: string, comentario: string) {
    onChange(elegidas.map((o) => (o.valor === valor ? { ...o, comentario } : o)));
  }

  return (
    <fieldset>
      <legend className="text-sm font-medium text-gray-700">
        ¿Qué te dijeron los que NO compraron?
      </legend>
      <p className="mb-2 text-xs text-gray-500">
        Marca todas las que escuchaste, y cuéntanos de cada una.
      </p>
      {opciones.map((o) => {
        const elegida = elegidas.find((x) => x.valor === o.valor);
        // Solo «otra» exige texto; lo valida `loQueFaltaEnCierre`, acá únicamente se avisa.
        const obligatorio = o.valor === 'otra';
        return (
          <div key={o.valor}>
            <div className="flex min-h-[44px] items-center gap-3">
              <input
                id={`obj-${o.valor}`}
                type="checkbox"
                checked={elegida !== undefined}
                onChange={(e) => alternar(o.valor, e.target.checked)}
                className="h-5 w-5 shrink-0"
              />
              <label htmlFor={`obj-${o.valor}`} className="flex-1 py-2 text-sm text-gray-800">
                {o.etiqueta}
              </label>
            </div>
            {elegida && o.pregunta !== '' && (
              <div className="mb-3 ml-8">
                <label
                  className="mb-1 block text-xs font-medium text-gray-700"
                  htmlFor={`obj-${o.valor}-det`}
                >
                  {o.pregunta}
                  {obligatorio && <span className="text-red-600"> (obligatorio)</span>}
                </label>
                <textarea
                  id={`obj-${o.valor}-det`}
                  rows={2}
                  aria-required={obligatorio}
                  value={elegida.comentario}
                  onChange={(e) => comentar(o.valor, e.target.value)}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            )}
          </div>
        );
      })}
    </fieldset>
  );
}

const VACIO = {
  quiebre: 'ninguno',
  quiebre_skus: [] as string[],
  exhibicion_adicional: 'no',
  precio_referencia: '',
  ejecucion_comentario: '',
  competencia_impulsadora: 'no',
  competencia_cual: '',
  competencia_exhibicion: '',
  competencia_precio: '',
  competencia_comentario: '',
  // Vacío, no «ninguna»: no hay respuesta todavía, y la ausencia ya significa que no hubo objeciones.
  objeciones: [] as ObjecionElegida[],
  incidencia: 'ninguna',
  incidencia_detalle: '',
  espacio: '',
  mercaderista: '',
  // Los totales no se teclean: se calculan al enviar desde los conteos por premio de abajo.
  premios_entregados: '',
  premios_quedan: '',
  shaker_entregados: '',
  porta_entregados: '',
  vaso_entregados: '',
  toalla_entregados: '',
  ventilador_entregados: '',
  shaker_quedan: '',
  porta_quedan: '',
  vaso_quedan: '',
  toalla_quedan: '',
  ventilador_quedan: '',
  merch_foto: '',
  // Precios en góndola (pedido de Bimbo del 27-oct): los que ella fue agregando, uno por uno.
  precios: [] as PrecioGondola[],
};

export default function CierreDeTurnoForm() {
  const [fecha, setFecha] = useState(hoyLima());
  const { roles } = useAuth();
  /* Mismo criterio que el banco de pruebas de «Mi turno»: quien administra tiene que poder
     mirar los formularios sin fabricarse una asignación real para cada campaña. */
  const esOficina = roles.some((r) => r === 'admin' || r === 'coordinador' || r === 'comercial');
  const [campanias, setCampanias] = useState<CampaniaDeImpulso[]>([]);
  /** Campaña que se está MIRANDO. Vacío = la del turno, que es lo normal. */
  const [viendo, setViendo] = useState('');
  const [turno, setTurno] = useState<MyShiftRow | null>(null);
  /** Todos los del día. Se guarda la lista entera porque con más de uno hay que elegir. */
  const [turnosDelDia, setTurnosDelDia] = useState<MyShiftRow[]>([]);
  const [skus, setSkus] = useState<SkuDeImpulso[]>([]);
  const [unidades, setUnidades] = useState<Record<string, string>>({});
  const [form, setForm] = useState({ ...VACIO });
  const [yaEsta, setYaEsta] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [listo, setListo] = useState(false);
  /** Dejó un precio escrito sin tocar «Agregar»: no se envía, o se perdería sin que lo sepa. */
  const [precioSinAgregar, setPrecioSinAgregar] = useState(false);
  /** Los días que puede elegir (los de sus turnos de estos días). `null` = calendario libre. */
  const [fechasMias, setFechasMias] = useState<string[] | null>(null);
  /** Lo que ya respondió. Pedido del usuario: poder ver su propio historial. */
  const [historial, setHistorial] = useState<MiCierre[]>([]);

  const recargar = useCallback(async () => {
    setCargando(true);
    setError('');
    setListo(false);
    setForm({ ...VACIO });
    setUnidades({});
    setViendo('');
    try {
      const turnos = await getMyShifts(fecha);
      setTurnosDelDia(turnos);
      /* Con varios turnos se toma el primero como punto de partida, pero abajo se puede
         cambiar: antes se quedaba con este en silencio y el cierre salía de otra campaña. */
      const elegido = turnos[0] ?? null;
      setTurno(elegido);
      if (elegido) {
        const campaignId = elegido.position.campaign_id;
        setSkus(await listSkusDeImpulso(campaignId, fecha));
        setYaEsta(await yaRegistroCierre(campaignId, fecha));
      } else {
        setSkus([]);
        setYaEsta(false);
      }
      setHistorial(await listMisCierres());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar tu turno.');
    } finally {
      setCargando(false);
    }
  }, [fecha]);

  useEffect(() => { void recargar(); }, [recargar]);

  /* Los días que puede elegir: los de sus turnos de estos días (`domain/fechasDeCierre.ts`, usuario 25-oct). Quien
     administra sigue con el calendario libre, y si la lista no carga también: mejor un calendario que no poder cerrar. */
  useEffect(() => {
    if (esOficina) return;
    const hoy = hoyLima();
    getMyShiftsRange(restarDias(hoy, DIAS_PARA_CERRAR - 1), hoy)
      .then((turnos) => {
        const fechas = fechasParaElegir(turnos, hoy);
        setFechasMias(fechas);
        if (fechas.length > 0 && !fechas.includes(hoy)) setFecha(fechas[fechas.length - 1]);
      })
      .catch(() => setFechasMias(null));
  }, [esOficina]);

  /* La lista de campañas se pide una vez y solo para oficina. */
  useEffect(() => {
    if (!esOficina) return;
    void listCampaniasDeImpulso().then(setCampanias).catch(() => undefined);
  }, [esOficina]);

  /* Cambiar de campaña redibuja el formulario entero: productos, precios y preguntas. */
  const verCampania = useCallback(async (id: string) => {
    setViendo(id);
    setForm({ ...VACIO });
    const campaignId = id || turno?.position.campaign_id;
    if (!campaignId) { setSkus([]); return; }
    setCargando(true);
    try {
      setSkus(await listSkusDeImpulso(campaignId, fecha));
      setUnidades({});
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar los productos.');
    } finally {
      setCargando(false);
    }
  }, [turno, fecha]);

  /** La campaña que manda en lo que se dibuja: la que se está mirando, o la del turno. */
  const campaniaVista = campanias.find((c) => c.id === viendo) ?? null;

  /** Cambiar de turno cambia de campaña, así que los productos y el «ya registrado»
   *  se vuelven a pedir. Sin esto quedarían los de la campaña anterior. */
  const elegirTurno = useCallback(async (id: string) => {
    const t = turnosDelDia.find((x) => x.id === id) ?? null;
    setTurno(t);
    setForm({ ...VACIO });
    if (!t) { setSkus([]); setYaEsta(false); return; }
    setCargando(true);
    try {
      setSkus(await listSkusDeImpulso(t.position.campaign_id, fecha));
      setYaEsta(await yaRegistroCierre(t.position.campaign_id, fecha));
      setUnidades({});
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar los productos.');
    } finally {
      setCargando(false);
    }
  }, [turnosDelDia, fecha]);

  function set<K extends keyof typeof VACIO>(k: K, v: (typeof VACIO)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  /** La campaña entrega premios en el punto: hay que preguntar por el merch.
   *  Sale de la campaña que se está MIRANDO, para que el banco de pruebas muestre de
   *  verdad el formulario de la otra campaña y no una mezcla. */
  const conCanje = campaniaVista
    ? campaniaVista.cierreConCanje
    : turno?.position.campaign.cierre_con_canje === true;
  /** La marca también decide el cuestionario: Takis no tiene canje y no es fijas. Mismo
   *  criterio que `conCanje`, la campaña mirada antes que la del turno. */
  const marca = campaniaVista ? campaniaVista.brand : turno?.position.campaign.brand;
  /** Y el cliente: P&G tiene su propio cuestionario, sea cual sea la marca (28-oct). */
  const cliente = campaniaVista ? campaniaVista.client : turno?.position.campaign.client;
  /** Las preguntas que le tocan a esta campaña, no las de la de al lado. */
  const cuestionario = cuestionarioDe(conCanje, marca, cliente);
  const esTakis = /takis/i.test(marca ?? '');
  /** Precios en góndola: Vital y fijas (Bimbo menos Takis). */
  const conPrecios = pideBarrido(cliente, marca);

  /**
   * La foto del merch va como data URI dentro del mismo envío, no a un bucket.
   *
   * Es a propósito: si fuera una subida aparte, el cierre podría quedar registrado y la foto no,
   * y nadie se enteraría. Así o entran los dos o no entra ninguno. Se comprime primero porque en
   * campo hay celulares de gama baja y señal mala.
   */
  async function elegirFotoDelMerch(archivo: File | undefined) {
    if (!archivo) return;
    setError('');
    try {
      const blob = await compressImage(archivo);
      const dataUrl = await new Promise<string>((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(String(fr.result));
        fr.onerror = () => rej(new Error('no se pudo leer la foto'));
        fr.readAsDataURL(blob);
      });
      set('merch_foto', dataUrl);
    } catch {
      setError('No se pudo preparar la foto. Intenta con otra.');
    }
  }

  const ventas: VentaSku[] = skus.map((s) => ({
    sku_code: s.code,
    unidades: unidades[s.code] === undefined || unidades[s.code].trim() === ''
      ? 0
      : Number(unidades[s.code]),
  }));

  async function enviar() {
    const cierre: CierreDeTurno = {
      campaign_id: turno?.position.campaign_id ?? '',
      pdv_id: turno?.position.pdv.id ?? '',
      assignment_id: turno?.id ?? null,
      fecha,
      ventas,
      ...form,
      // Los totales son la suma de los cuatro conteos. Se calculan acá y no se le piden a ella:
      // sumar dos números a mano al final de nueve horas es una fuente de error gratuita.
      premios_entregados: conCanje ? String(entregadosEnTotal(form)) : form.premios_entregados,
      premios_quedan: conCanje ? String(quedanEnTotal(form)) : form.premios_quedan,
      precios: conPrecios ? preciosParaEnviar(form.precios) : [],
    };
    const falta = (conPrecios && precioSinAgregar
      ? 'En «Precios en góndola» dejaste un producto sin agregar: toca «Agregar a la lista» o elige «Elige un producto…».'
      : null)
      ?? loQueFaltaEnCierre(cierre, { conCanje })
      ?? (esTakis ? faltaTakis(form.ejecucion_comentario) : null)
      ?? (skus.length === 0 ? 'No se cargaron los productos. Vuelve a cargar el formulario antes de enviar.' : null);
    if (falta) { setError(falta); return; }
    setEnviando(true);
    setError('');
    try {
      await registrarCierreDeTurno(cierre);
      setListo(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo registrar tu cierre.');
    } finally {
      setEnviando(false);
    }
  }

  if (listo) {
    /* Los días que le faltan cerrar, para que pase al siguiente sin buscarlo (25-oct: se anularon los del sábado y
       cada impulsadora llenó dos cierres seguidos). */
    const pendientes = (fechasMias ?? []).filter((f) => f !== fecha && !historial.some((h) => h.fecha === f));
    return (
      <div className="mx-auto max-w-md p-4">
        <div className="rounded-xl border border-green-200 bg-green-50 p-5 text-center">
          <p className="text-lg font-bold text-green-800">Cierre registrado</p>
          <p className="mt-2 text-sm text-green-900">
            Reportaste {totalUnidades(ventas)} unidades vendidas el{' '}
            {formatInTimeZone(new Date(`${fecha}T12:00:00Z`), TZ, 'dd/MM/yyyy')}.
          </p>
        </div>
        {pendientes.map((f) => (
          <button
            key={f} type="button" onClick={() => { setListo(false); setFecha(f); }}
            className="mt-4 w-full rounded-xl bg-indigo-600 py-4 text-base font-bold text-white"
          >
            Llenar el cierre del {nombreDeFecha(f, hoyLima())}
          </button>
        ))}
        <Link to="/movil/cierre-de-turno" onClick={() => { setListo(false); void recargar(); }}
          className="mt-4 block py-3 text-center text-sm text-blue-700 underline">
          Ver mis cierres registrados
        </Link>
        <Link to="/movil" className="block py-2 text-center text-sm text-blue-700 underline">
          Volver a mi turno
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md p-4">
      <h1 className="mb-1 text-xl font-bold text-gray-900">Cierre de turno</h1>
      <p className="mb-4 text-sm text-gray-600">
        Llénalo al terminar tu jornada, después de marcar tu salida.
      </p>

      {esOficina && campanias.length > 0 && (
        <div className="mb-4 rounded-xl border border-dashed border-gray-300 p-3">
          <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="ver-campania">
            Banco de pruebas · ver el formulario de otra campaña
          </label>
          <select
            id="ver-campania"
            value={viendo}
            onChange={(e) => void verCampania(e.target.value)}
            className="min-h-[44px] w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm"
          >
            <option value="">Mi turno{turno ? ` — ${turno.position.campaign.brand}` : ''}</option>
            {campanias.map((c) => (
              <option key={c.id} value={c.id}>{c.brand} — {c.name}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            {viendo
              ? 'Estás viendo el formulario de otra campaña. Puedes tocarlo todo, pero no se envía: un cierre guardado contra una campaña que no trabajaste ensucia los números.'
              : 'Solo lo ves tú y quien administra. Elige una campaña para revisar su formulario.'}
          </p>
        </div>
      )}

      <Grupo titulo="¿De qué día es tu cierre?">
        {/* Solo los días de sus turnos, para que no ponga otra fecha (usuario, 25-oct). Un día ya cerrado se puede
            elegir igual: al elegirlo sale «Ya registraste tu cierre de esa fecha» y no deja llenarlo otra vez. */}
        {!esOficina && fechasMias && fechasMias.length > 0 ? (
          <>
            <div role="radiogroup" aria-label="Día del cierre" className="grid gap-2">
              {fechasMias.map((f) => (
                <button
                  key={f} type="button" role="radio" aria-checked={f === fecha}
                  onClick={() => setFecha(f)}
                  className={`min-h-[44px] w-full rounded border px-3 py-2 text-left text-sm ${
                    f === fecha
                      ? 'border-indigo-600 bg-indigo-50 font-semibold text-indigo-900'
                      : 'border-gray-300 bg-white text-gray-800'}`}
                >
                  {nombreDeFecha(f, hoyLima())}
                  {historial.some((h) => h.fecha === f) && (
                    <span className="ml-1 text-xs font-normal text-gray-500">· ya enviado</span>
                  )}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Solo aparecen los días de tus turnos. Si te falta el cierre de un día anterior, elígelo acá.
            </p>
          </>
        ) : (
          <>
            <input
              type="date"
              aria-label="Fecha del turno"
              value={fecha}
              max={hoyLima()}
              onChange={(e) => setFecha(e.target.value)}
              className="min-h-[44px] w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
            {/* Se puede elegir otra fecha porque a veces no se alcanza a llenar el mismo día. */}
            <p className="mt-1 text-xs text-gray-500">
              Por defecto es hoy. Si estás llenando el de un día anterior, cámbialo.
            </p>
          </>
        )}
        {cargando && <p className="mt-2 text-sm text-gray-500">Buscando tu turno…</p>}
        {!cargando && turno && turnosDelDia.length === 1 && (
          <p className="mt-2 rounded bg-gray-50 p-2 text-sm text-gray-700">
            {turno.position.pdv.name} · {turno.position.start_time.slice(0, 5)}–
            {turno.position.end_time.slice(0, 5)} · {turno.position.campaign.brand}
          </p>
        )}
        {/* Con más de un turno hay que decir CUÁL se está cerrando: cada campaña tiene sus
            productos y sus preguntas, y llenar el del turno equivocado no se nota hasta
            que alguien cruza los números. */}
        {!cargando && turnosDelDia.length > 1 && (
          <div className="mt-2">
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="cual-turno">
              Tienes {turnosDelDia.length} turnos ese día. ¿Cuál estás cerrando?
            </label>
            <select
              id="cual-turno"
              value={turno?.id ?? ''}
              onChange={(e) => void elegirTurno(e.target.value)}
              className="min-h-[44px] w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              {turnosDelDia.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.position.campaign.brand} · {t.position.pdv.name} ·{' '}
                  {t.position.start_time.slice(0, 5)}–{t.position.end_time.slice(0, 5)}
                </option>
              ))}
            </select>
          </div>
        )}
        {!cargando && !turno && (
          <p className="mt-2 rounded bg-amber-50 p-2 text-sm text-amber-800">
            No tienes turno registrado esa fecha. Revisa el día o avisa a tu supervisor.
          </p>
        )}
        {yaEsta && (
          <p className="mt-2 rounded bg-amber-50 p-2 text-sm text-amber-800">
            Ya registraste tu cierre de esa fecha. Si necesitas corregirlo, avisa a tu supervisor.
          </p>
        )}
      </Grupo>

      {(turno || campaniaVista) && (viendo || !yaEsta) && (
        <>
          <Grupo titulo="¿Cuánto vendiste hoy?">
            <p className="mb-3 text-xs text-gray-500">
              Pasa acá los palotes de tu papel. Si de algo no vendiste, déjalo en 0.
            </p>
            {/*
              Las líneas salen de los SKU de LA CAMPAÑA, no de una lista escrita acá.

              Estaban cableadas a `['Rapiditas', 'Tostadas']`, que son las de Impulso Fijo. Para
              Vital, cuyas líneas son «Vital» y «Cero Cero», el formulario no mostraba ni un solo
              producto: la persona llegaba al final sin nada que llenar y su cierre quedaba en
              cero. El 12-set, primer día de campaña, **doce impulsadoras registraron así**.

              `listSkusDeImpulso` ya devuelve ordenado por `sort`, así que el Set conserva ese
              orden y no hace falta volver a ordenar.
            */}
            {[...new Set(skus.map((s) => s.linea))].map((linea) => {
              const delLinea = skus.filter((s) => s.linea === linea);
              if (delLinea.length === 0) return null;
              return (
                <div key={linea} className="mb-3">
                  <p className="mb-1 text-sm font-semibold text-gray-800">{linea}</p>
                  {delLinea.map((s) => (
                    <div key={s.code} className="mb-2 flex items-center gap-2">
                      <label className="flex-1 text-sm text-gray-700" htmlFor={`sku-${s.code}`}>
                        {s.label}
                      </label>
                      <input
                        id={`sku-${s.code}`}
                        type="number"
                        inputMode="numeric"
                        min={0}
                        placeholder="0"
                        value={unidades[s.code] ?? ''}
                        onChange={(e) =>
                          setUnidades((u) => ({ ...u, [s.code]: e.target.value }))}
                        className="min-h-[44px] w-24 rounded border border-gray-300 px-3 py-2 text-right text-sm"
                      />
                    </div>
                  ))}
                </div>
              );
            })}
            <p className="mt-2 text-right text-sm font-bold text-gray-900">
              Total: {totalUnidades(ventas)} unidades
            </p>
          </Grupo>

          {/*
            Solo para campañas que entregan premios en el punto. Las cuatro preguntas salen de la
            capacitación de Bimbo del 11-set: dónde pararse (cabeceras, rumas y laterales, que no
            son la góndola), si apareció el mercaderista (sin él no hay reposición y en Tottus es
            donde más falta), el conteo de premios a mano —el único cruce contra lo que dice el
            juego— y la foto de lo que se deja en tienda, que la pidió el cliente con nombre y
            apellido porque el merch sobrante se queda en góndola sin responsable.
          */}
          {conCanje && (
            <Grupo titulo="El merch y tu espacio">
              <Elegir
                id="espacio" label="¿En qué espacio trabajaste hoy?"
                valor={form.espacio} opciones={[{ valor: '', etiqueta: 'Elige…' }, ...ESPACIO]}
                onChange={(v) => set('espacio', v)}
              />
              <Elegir
                id="mercaderista" label="¿Estuvo el mercaderista de Bimbo?"
                valor={form.mercaderista}
                opciones={[{ valor: '', etiqueta: 'Elige…' }, ...MERCADERISTA]}
                onChange={(v) => set('mercaderista', v)}
              />

              {/*
                * Un renglón por premio, no un total. Cuando el conteo de ella no cuadra con el
                * del juego hay que saber QUÉ falta para reponerlo, y el total suelto no lo dice.
                */}
              <fieldset className="mb-3">
                <legend className="mb-2 block text-sm font-medium text-gray-700">
                  Cuenta tu merch, premio por premio
                </legend>
                <div className="grid grid-cols-[1fr_5rem_5rem] items-center gap-x-2 gap-y-2">
                  <span aria-hidden="true" />
                  <span className="text-center text-xs font-medium text-gray-500">Entregué</span>
                  <span className="text-center text-xs font-medium text-gray-500">Me quedan</span>

                  {/* Los cinco premios del juego, no solo los dos primeros (usuario, 23-oct). */}
                  {PREMIOS_DEL_MERCH.map(({ clave: premio, nombre }) => {
                    return (
                      <Fragment key={premio}>
                        <span className="text-sm text-gray-800">{nombre}</span>
                        {(['entregados', 'quedan'] as const).map((cual) => {
                          const campo = `${premio}_${cual}` as const;
                          return (
                            <input
                              key={campo} id={campo} type="number" inputMode="numeric" min={0}
                              aria-label={`${nombre}, ${cual === 'quedan' ? 'me quedan' : 'entregué'}`}
                              value={form[campo]}
                              onChange={(e) => set(campo, e.target.value)}
                              className="min-h-[44px] w-full rounded border border-gray-300 px-2 py-2 text-center text-sm"
                            />
                          );
                        })}
                      </Fragment>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-gray-600">
                  Van <b>{entregadosEnTotal(form)}</b> entregados y te quedan{' '}
                  <b>{quedanEnTotal(form)}</b> en total.
                </p>
              </fieldset>
              <p className="mb-3 text-xs text-gray-500">
                Cuéntalos tú, aunque el sistema también lleve la cuenta: si los dos números no
                coinciden queremos enterarnos el mismo día, no a fin de mes.
              </p>

              <div className="mb-1">
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="merch-foto">
                  Foto del merch que dejas en tienda <span className="font-normal text-gray-500">· opcional</span>
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  Es tu respaldo de cuántas unidades quedaron. Si puedes, mándala; si no se deja
                  tomar, envía igual tu cierre y avisa a tu supervisor.
                </p>
                <input
                  id="merch-foto" type="file" accept="image/*" capture="environment"
                  onChange={(e) => void elegirFotoDelMerch(e.target.files?.[0])}
                  className="w-full text-sm"
                />
                {form.merch_foto && (
                  <div className="mt-2">
                    <img
                      src={form.merch_foto} alt="Merch que queda en tienda"
                      className="max-h-56 rounded border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => set('merch_foto', '')}
                      className="mt-1 text-xs font-medium text-gray-600 underline"
                    >
                      Cambiar la foto
                    </button>
                  </div>
                )}
              </div>
            </Grupo>
          )}

          <Grupo titulo="La tienda">
            <Elegir
              id="quiebre" label="¿Faltó producto en góndola?"
              valor={form.quiebre} opciones={QUIEBRE}
              onChange={(v) => set('quiebre', v)}
            />
            {form.quiebre === 'algunos' && (
              <div className="mb-3">
                <p className="mb-1 text-sm font-medium text-gray-700">¿Cuáles faltaron?</p>
                {skus.map((s) => (
                  <label key={s.code} className="flex min-h-[40px] items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.quiebre_skus.includes(s.code)}
                      onChange={(e) =>
                        set('quiebre_skus', e.target.checked
                          ? [...form.quiebre_skus, s.code]
                          : form.quiebre_skus.filter((c) => c !== s.code))}
                    />
                    {s.linea} · {s.label}
                  </label>
                ))}
              </div>
            )}
            <Elegir
              id="exhibicion" label="¿Tuviste exhibición además de la góndola?"
              valor={form.exhibicion_adicional} opciones={EXHIBICION}
              onChange={(v) => set('exhibicion_adicional', v)}
            />
            <div className="mb-3">
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="precio">
                {cuestionario.precioPropio.label}
              </label>
              <input
                id="precio" type="text" inputMode="decimal"
                placeholder={cuestionario.precioPropio.placeholder}
                value={form.precio_referencia}
                onChange={(e) => set('precio_referencia', e.target.value)}
                className="min-h-[44px] w-32 rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            {!esTakis && (
            <Texto
              id="ejec-com" label="¿Algo más que contar de la tienda?"
              ayuda="Alguna promoción, un cambio de espacio, algo que te llamó la atención."
              valor={form.ejecucion_comentario}
              onChange={(v) => set('ejecucion_comentario', v)}
            />
            )}
          </Grupo>

          {esTakis && (
            <TakisEjecucion valor={form.ejecucion_comentario}
              onChange={(v) => set('ejecucion_comentario', v)} />
          )}

          <Grupo titulo="La competencia">
            <Elegir
              id="comp" label="¿Había impulsadora de otra marca?"
              valor={form.competencia_impulsadora} opciones={cuestionario.competencia}
              onChange={(v) => set('competencia_impulsadora', v)}
            />
            {form.competencia_impulsadora === 'otra' && (
              <div className="mb-3">
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="comp-cual">
                  ¿De qué marca?
                </label>
                <input
                  id="comp-cual" type="text" value={form.competencia_cual}
                  onChange={(e) => set('competencia_cual', e.target.value)}
                  className="min-h-[44px] w-full rounded border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            )}
            <div className="mb-3">
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="comp-exh">
                ¿La competencia tenía exhibición adicional? ¿Dónde?
              </label>
              <input
                id="comp-exh" type="text" placeholder="Cabecera, isla, cerca de caja…"
                value={form.competencia_exhibicion}
                onChange={(e) => set('competencia_exhibicion', e.target.value)}
                className="min-h-[44px] w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="mb-3">
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="comp-precio">
                {cuestionario.precioCompetencia.label}
              </label>
              <input
                id="comp-precio" type="text" inputMode="decimal"
                placeholder={cuestionario.precioCompetencia.placeholder}
                value={form.competencia_precio}
                onChange={(e) => set('competencia_precio', e.target.value)}
                className="min-h-[44px] w-32 rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            {/* Texto abierto pedido por el usuario: «a veces se encuentran cosas disruptivas que ni
                siquiera en el mapa la tenemos». Una lista cerrada solo recoge lo ya imaginado. */}
            <Texto
              id="comp-com" label="¿Viste algo de la competencia que valga contar?"
              ayuda="Una promoción rara, un producto nuevo, un precio que sorprende, lo que sea."
              valor={form.competencia_comentario}
              onChange={(v) => set('competencia_comentario', v)}
            />
          </Grupo>

          {conPrecios && (
            <PreciosEnGondola
              valor={form.precios}
              onChange={(v) => set('precios', v)}
              onPendiente={setPrecioSinAgregar}
            />
          )}

          <Grupo titulo="Los clientes">
            <Objeciones
              elegidas={form.objeciones}
              onChange={(v) => set('objeciones', v)}
              opciones={cuestionario.objeciones}
            />
          </Grupo>

          {/* Zona propia, no un apéndice de otra pregunta: es lo que explica un turno flojo sin
              culpar a la persona, y estaba escondida al final de otro bloque. */}
          <Grupo titulo="Incidencias">
            <Elegir
              id="incidencia" label="¿Algo te impidió trabajar normal?"
              valor={form.incidencia} opciones={INCIDENCIA}
              onChange={(v) => set('incidencia', v)}
            />
            {form.incidencia !== 'ninguna' && (
              <Texto
                id="inc-det" label="Cuéntanos en una línea qué pasó"
                valor={form.incidencia_detalle}
                onChange={(v) => set('incidencia_detalle', v)}
              />
            )}
          </Grupo>

          {error && <p className="mb-3 rounded bg-red-50 p-3 text-sm text-red-700">{error}</p>}

          <button
            type="button"
            onClick={() => void enviar()}
            /* No se envía mientras se mira el formulario de otra campaña: un cierre
               guardado contra una campaña que no se trabajó ensucia el cruce. */
            disabled={cargando || enviando || !!viendo}
            className="w-full rounded-xl bg-indigo-600 py-5 text-lg font-bold text-white disabled:bg-gray-300"
          >
            {enviando ? 'Enviando…' : 'Enviar mi cierre'}
          </button>
        </>
      )}

      {error && !turno && <p className="mt-3 rounded bg-red-50 p-3 text-sm text-red-700">{error}</p>}

      {historial.length > 0 && (
        <Grupo titulo="Mis cierres registrados">
          <ul className="text-sm">
            {historial.map((h) => (
              <li key={h.id} className="flex justify-between border-b border-gray-100 py-2 last:border-0">
                <span>
                  {formatInTimeZone(new Date(`${h.fecha}T12:00:00Z`), TZ, 'dd/MM')}
                  <span className="block text-xs text-gray-500">{h.tienda}</span>
                </span>
                <span className="font-bold text-gray-900">{h.unidades} un.</span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-gray-500">
            Si algo quedó mal, avisa a tu supervisor: no se puede editar desde acá.
          </p>
        </Grupo>
      )}

      <Link to="/movil" className="mt-4 block py-3 text-center text-sm text-blue-700 underline">
        Volver a mi turno
      </Link>
    </div>
  );
}
